/**
 * extensions/dicom-rt/src/components/RTEdit/sections/ROIContourSection.js
 *
 * v4 PATCH:
 *   - Sidebar tracking-type filter now uses the 4-category RECIP set via
 *     getTrackingTypes('RECIP10'):
 *       All Lesions / Lesions / New Lesions / Blood Pool / Other / All ROIs /
 *       Selected
 *   - Filter logic delegates to matchTrackingTypeFilter (single source of
 *     truth, shared with LongitudinalStudy).
 *
 * No PSMA Status filter here — the sidebar is link-level, not per-tp. PSMA
 * status filtering lives in the Longitudinal Study windows.
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cornerstoneTools from 'cornerstone-tools';

import {
  Icon,
  ScrollableArea,
  TableList,
  Grid,
  Box,
  Button,
  IconButton,
  ToolTip,
  Select,
  TableListItem,
} from '@platform/ui';
import usePrimary from '../../../hooks/usePrimary';
import { NewROIForm } from '../forms/NewROIForm';
import { ROIContourItem } from '../sections/ROIContourItem';
import FooterButton from './FooterButton';

import module from '../../../modules/dicom-measurement/src';
const { guideline } = module;
const { getTrackingTypes, matchTrackingTypeFilter } = guideline;

function getActiveGuidelineId() {
  try {
    const editModule = cornerstoneTools.getModule('rtstruct-edit');
    const criteria = editModule?.getters?.criteria?.();
    return criteria?.guidelineId;
  } catch (e) {
    return undefined;
  }
}

export const ROIContourSection = ({
  mode,
  StructureSetUID,
  StructureSetDescription,
  onConfirm,
  ROIContours,
  selectedROIContour,
  getNewROIName,
  addNewROIContour,
  copyROIContour,
  navigateToROIContour,
  onROIClick,
  onROIUpdate,
  onROIDelete,
  isAllDisplayed,
  onDisplayAllStatusChange,
  onComputingStatusChange,
  isLinking,
  onLinkingStatusChange,
  getROILinked,
  onROILink,
  onROIUnlink,
  getROIChecked,
  onROICheck,
}) => {
  const bottomRef = useRef(null);
  const guidelineId = getActiveGuidelineId();

  /* The 'all-rois' value only makes sense under RECIP10 longitudinal mode
   * (matchTrackingTypeFilter handles it). For other mode/guideline combos
   * the legacy switch doesn't recognise 'all-rois' and would hide everything,
   * so we keep 'all' there. */
  const isRecipLongitudinal =
    mode === 'longitudinal' && guidelineId === 'RECIP10';

  const [selectedTrackingType, setSelectedTrackingType] = useState(
    isRecipLongitudinal ? 'all-rois' : 'all'
  );
  const [isScrolling, setIsScrolling] = useState(false);
  const isPrimary = usePrimary();
  const disabled = mode === 'readonly' || (mode === 'tracking' && !isPrimary);

  /* Snap to the right default when crossing the (longitudinal + RECIP10)
   * boundary. Two scenarios this catches:
   *   - First mount before criteria loaded → guidelineId was undefined →
   *     initial useState fell back to 'all' → criteria loads later → without
   *     this effect, state stays 'all' which under RECIP excludes BP.
   *   - User switches guideline via "Update Criteria" while sidebar is open
   *     → component remains mounted → useState ignored on re-render.
   *
   * Only snap when the previous value was the "matching default" of the old
   * state — never override a user's explicit dropdown choice. */
  const prevRecipLongRef = useRef(isRecipLongitudinal);
  useEffect(() => {
    const wasRecipLong = prevRecipLongRef.current;
    if (wasRecipLong === isRecipLongitudinal) return;
    if (isRecipLongitudinal && selectedTrackingType === 'all') {
      setSelectedTrackingType('all-rois');
    } else if (!isRecipLongitudinal && selectedTrackingType === 'all-rois') {
      setSelectedTrackingType('all');
    }
    prevRecipLongRef.current = isRecipLongitudinal;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecipLongitudinal]);

  useEffect(() => {
    if (isScrolling) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    setIsScrolling(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ROIContours]);

  return (
    <>
      <div style={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          style={{ marginBottom: '10px' }}
        >
          <DisplayAllButton
            isAllDisplayed={isAllDisplayed}
            onDisplayAllStatusChange={onDisplayAllStatusChange}
          />
          <NewROIForm
            mode={mode}
            guidelineId={guidelineId}
            defaultName={getNewROIName()}
            onFinished={rois => {
              addNewROIContour(rois);
              setIsScrolling(true);
            }}
            disabled={disabled || isLinking}
          />
          <ComputeButton
            onComputingStatusChange={onComputingStatusChange}
            disabled={disabled || isLinking}
          />
          {mode === 'tracking' && (
            <LinkButton
              onLinkingStatusChange={onLinkingStatusChange}
              disabled={isLinking}
            />
          )}
        </Box>
        {['tracking', 'longitudinal'].includes(mode) && (
          <TrackingTypeFilter
            selectedTrackingType={selectedTrackingType}
            setSelectedTrackingType={setSelectedTrackingType}
            mode={mode}
            guidelineId={guidelineId}
          />
        )}
        {['readonly', 'contouring', 'tracking'].includes(mode) && (
          <StructureSetFilter label={StructureSetDescription} />
        )}
        <ScrollableArea>
          <TableList headless style={{ margin: '0 0 100px' }}>
            {ROIContours.filter(roi => {
              if (mode === 'longitudinal') {
                // Delegate all longitudinal filtering to the central function —
                // handles RECIP10 and non-RECIP (all / all-rois / other / target /
                // non-target / new / measurable / selected) consistently.
                return matchTrackingTypeFilter(
                  roi,
                  selectedTrackingType,
                  guidelineId,
                  selectedROIContour?.ROINumber
                );
              }
              /* Legacy logic for tracking mode (tracked / ex / organ options
               * are not part of matchTrackingTypeFilter). */
              switch (selectedTrackingType) {
                case 'all':
                  return true;
                case 'measurable':
                  return ['target', 'non-target'].includes(roi.trackingType);
                case 'new':
                  return [
                    'new',
                    'new-lesion-target',
                    'new-lesion-non-target',
                  ].includes(roi.trackingType);
                case 'lymph-node':
                  return selectedTrackingType === roi.bodyPart;
                case 'organ':
                  return 'ORGAN' === roi.RTROIObservations.RTROIInterpretedType;
                default:
                  return selectedTrackingType === roi.trackingType;
              }
            }).map((roi, idx) => (
              <ROIContourItem
                key={StructureSetUID + roi.ROINumber + roi.trackingType}
                id={StructureSetUID + roi.ROINumber + roi.trackingType}
                idx={idx}
                mode={mode}
                roi={roi}
                selected={roi.ROINumber === selectedROIContour?.ROINumber}
                onConfirm={onConfirm}
                onClick={onROIClick}
                onUpdate={onROIUpdate}
                onDelete={onROIDelete}
                onCopy={(roi, idx) => {
                  copyROIContour(roi, idx);
                  setIsScrolling(true);
                }}
                onNavigate={navigateToROIContour}
                isLinking={isLinking}
                getLinked={getROILinked}
                onUnlink={onROIUnlink}
                getChecked={getROIChecked}
                onCheck={onROICheck}
              />
            ))}
            <div ref={bottomRef} />
          </TableList>
        </ScrollableArea>
      </div>
      {isLinking && (
        <div style={{ margin: 'auto 0 0 0', padding: '5px 0 0' }}>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <FooterButton onClick={() => onROILink(false)}>
                Cancel
              </FooterButton>
            </Grid>
            <Grid item xs={6}>
              <FooterButton onClick={() => onROILink(true)}>Link</FooterButton>
            </Grid>
          </Grid>
        </div>
      )}
    </>
  );
};
ROIContourSection.propTypes = {
  mode: PropTypes.string,
  StructureSetUID: PropTypes.string,
  StructureSetDescription: PropTypes.string,
  onConfirm: PropTypes.func,
  ROIContours: PropTypes.array,
  selectedROIContour: PropTypes.object,
  getNewROIName: PropTypes.func,
  addNewROIContour: PropTypes.func,
  copyROIContour: PropTypes.func,
  navigateToROIContour: PropTypes.func,
  onROIClick: PropTypes.func,
  onROIUpdate: PropTypes.func,
  onROIDelete: PropTypes.func,
  isAllDisplayed: PropTypes.bool,
  onDisplayAllStatusChange: PropTypes.func,
  onComputingStatusChange: PropTypes.func,
  isLinking: PropTypes.bool,
  onLinkingStatusChange: PropTypes.func,
  getROILinked: PropTypes.func,
  onROILink: PropTypes.func,
  onROIUnlink: PropTypes.func,
  getROIChecked: PropTypes.func,
  onROICheck: PropTypes.func,
};

const DisplayAllButton = ({ isAllDisplayed, onDisplayAllStatusChange }) => (
  <ToolTip title={isAllDisplayed ? 'Hide' : 'Display'}>
    <IconButton
      onClick={() => onDisplayAllStatusChange()}
      style={{ marginRight: '5px' }}
      color="secondary"
      variant="contained"
    >
      <Icon
        name={isAllDisplayed ? 'eye-on' : 'eye-off'}
        width="24px"
        height="24px"
        style={{ color: 'black', background: 'transparent' }}
      />
    </IconButton>
  </ToolTip>
);
DisplayAllButton.propTypes = {
  isAllDisplayed: PropTypes.bool,
  onDisplayAllStatusChange: PropTypes.func,
};

const ComputeButton = ({ onComputingStatusChange, disabled }) => (
  <Button
    disabled={disabled}
    onClick={() => onComputingStatusChange(true)}
    style={{ marginLeft: '5px' }}
    color="secondary"
    variant="contained"
    fullWidth
  >
    <Icon
      name="code"
      width="20px"
      height="20px"
      style={{ color: disabled ? '#5A5A5A' : 'black' }}
    />
    Calc
  </Button>
);
ComputeButton.propTypes = {
  onComputingStatusChange: PropTypes.func,
  disabled: PropTypes.bool,
};

const LinkButton = ({ onLinkingStatusChange, disabled }) => (
  <Button
    disabled={disabled}
    onClick={() => onLinkingStatusChange(true)}
    style={{ marginLeft: '5px' }}
    color="secondary"
    variant="contained"
    fullWidth
  >
    <Icon
      name="link"
      width="20px"
      height="20px"
      style={{ color: disabled ? '#5A5A5A' : 'black' }}
    />
    Link
  </Button>
);
LinkButton.propTypes = {
  onLinkingStatusChange: PropTypes.func,
  disabled: PropTypes.bool,
};

const TrackingTypeFilter = ({
  selectedTrackingType,
  setSelectedTrackingType,
  mode,
  guidelineId,
}) => {
  const legacyMapping = {
    contouring: [],
    tracking: [
      { id: 'all', label: 'All' },
      { id: 'tracked', label: 'Tracked' },
      { id: 'new', label: 'New' },
      { id: 'ex', label: 'Ex' },
      { id: 'organ', label: 'Organ' },
    ],
  };

  let types;
  if (mode === 'longitudinal') {
    types = getTrackingTypes(guidelineId).map(([id, label]) => ({ id, label }));
  } else {
    types = legacyMapping[mode] || [];
  }

  return (
    <Select
      value={selectedTrackingType}
      onChange={evt => setSelectedTrackingType(evt.target.value)}
      style={{ margin: '0 0 5px', minHeight: '40px' }}
    >
      {types.map(({ id, label }) => (
        <option key={id} value={id}>
          {label}
        </option>
      ))}
    </Select>
  );
};
TrackingTypeFilter.propTypes = {
  selectedTrackingType: PropTypes.string,
  setSelectedTrackingType: PropTypes.func,
  mode: PropTypes.string,
  guidelineId: PropTypes.string,
};

const StructureSetFilter = ({ label }) => (
  <TableListItem
    itemKey={0}
    itemIndex={''}
    itemClass={''}
    itemMeta={
      <IconButton
        disabled
        onClick={() => {}}
        color="primary"
        size="small"
        style={{ background: 'transparent' }}
      >
        <Icon name={'menu'} width="18px" height="18px" />
      </IconButton>
    }
    itemMetaClass="item-color-section"
    itemSelected={false}
    onItemClick={() => {}}
    style={{ padding: '0 4px 0 0' }}
  >
    {label}
  </TableListItem>
);
StructureSetFilter.propTypes = { label: PropTypes.string };
